import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatLongDate } from "@/lib/utils";
import bannerAsset from "@/assets/banner-episodio-59.png.asset.json";

const FALLBACK_URL = "https://www.youtube.com/@NBTPodcast/videos";

interface LatestVideo {
  youtubeUrl?: string;
  videoId?: string;
  title?: string;
  description?: string;
  thumbnail?: string;
  publishedAt?: string;
}

const HeroSection = () => {
  const [video, setVideo] = useState<LatestVideo | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import("@/integrations/supabase/client")
      .then(({ supabase }) => supabase.functions.invoke("youtube-latest-video", { body: {} }))
      .then(({ data }) => {
        if (!cancelled && data && !data.error) setVideo(data as LatestVideo);
      })
      .catch(() => {
        /* fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const videoUrl = video?.youtubeUrl || FALLBACK_URL;
  const title = video?.title || "Nuevo episodio de NBT Podcast";
  const description =
    video?.description?.split("\n")[0]?.slice(0, 120) ||
    "Conversaciones sin filtros con las mentes más influyentes de Latinoamérica.";
  const heroImage =
    (video?.videoId && `https://i.ytimg.com/vi/${video.videoId}/maxresdefault.jpg`) ||
    video?.thumbnail ||
    bannerAsset.url;

  return (
    <section className="relative bg-background pt-6 md:pt-8 pb-4">
      <div className="site-wrap">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 pb-6 border-b border-border">
          <div className="shrink-0 md:w-48">
            <h1 className="font-serif text-2xl md:text-[28px] font-semibold leading-tight text-foreground">
              Último episodio
            </h1>
            <p className="mt-1 text-[11px] font-sans font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {formatLongDate(video?.publishedAt)}
            </p>
          </div>

          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 min-w-0 flex-1 md:max-w-xl"
          >
            {video?.thumbnail ? (
              <img
                src={video.thumbnail}
                alt=""
                className="w-14 h-14 rounded object-cover shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded bg-muted shrink-0" />
            )}
            <div className="min-w-0">
              <p className="font-serif text-base md:text-lg font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
                {title}
              </p>
              <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{description}</p>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-1 shrink-0">
            <span className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground">
              <ChevronLeft className="w-4 h-4" />
            </span>
            <span className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground">
              <ChevronRight className="w-4 h-4" />
            </span>
          </div>
        </div>

        {heroImage && (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Ver el nuevo episodio en YouTube"
            className="group relative block rounded-xl overflow-hidden mt-6 bg-muted"
          >
            <img
              src={heroImage}
              alt={title}
              width={1280}
              height={720}
              loading="eager"
              decoding="async"
              className="w-full h-auto aspect-video object-cover"
            />
          </a>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
